import { Form } from 'react-bootstrap';
import { displayName } from '../../utilities/conversions';
import { MEDIAURL } from '../../utilities/constants';
import { getTeamTricode } from './utils';
import styles from './SessionSetup.module.css';

export function TeamRow({
  sessionTeam,
  sessionRoster,
  squad,
  desiredSquads,
  pendingSquads,
  applying,
  lineups,
  hasDuplicateOrder,
  onSquadClick,
  onRosterChange,
  onLineupChange,
  onDelete,
}) {
  const st = sessionTeam;
  const sr = sessionRoster;
  const team = st.team;

  const tricode = team?.triCode || getTeamTricode(team?.name);

  // Parse team logo and colors
  const teamLogo = team?.logos
    ? JSON.parse(team.logos)?.metaData?.filename
    : null;
  const teamColors = team?.colors ? JSON.parse(team.colors) : [];
  const primaryColor = teamColors[0] || '#6b7280';

  // Get rosters for THIS team only
  const teamRosters = (team?.rosters?.items || [])
    .filter((r) => !r._deleted)
    .slice()
    .sort((a, b) => {
      if (a.default !== b.default) return b.default ? 1 : -1;
      const aDate = a.activeDate ? new Date(a.activeDate) : new Date(0);
      const bDate = b.activeDate ? new Date(b.activeDate) : new Date(0);
      if (aDate.getTime() !== bDate.getTime())
        return bDate.getTime() - aDate.getTime();
      return (a.title || '').localeCompare(b.title || '');
    });

  // Sort lineups by match quality
  const sortedLineups = [...lineups].sort((a, b) => {
    const aTeamMatch = a.teamId === team?.id ? 1 : 0;
    const bTeamMatch = b.teamId === team?.id ? 1 : 0;
    const aOrderMatch = a.order === st.order ? 1 : 0;
    const bOrderMatch = b.order === st.order ? 1 : 0;
    const aScore = aTeamMatch * 2 + aOrderMatch;
    const bScore = bTeamMatch * 2 + bOrderMatch;
    return bScore - aScore;
  });

  // Detect duplicate team+order combinations
  const teamOrderCounts = {};
  for (const l of lineups) {
    const key = `${l.teamId}-${l.order}`;
    teamOrderCounts[key] = (teamOrderCounts[key] || 0) + 1;
  }
  const hasDuplicateKey = (l) => teamOrderCounts[`${l.teamId}-${l.order}`] > 1;

  return (
    <div className={styles.teamRow}>
      {/* Order */}
      <span
        className={`${styles.teamOrder} ${
          hasDuplicateOrder ? styles.teamOrderDuplicate : ''
        }`}
        title={
          hasDuplicateOrder
            ? 'Duplicate order - multiple teams have the same order'
            : undefined
        }
      >
        {st.order + 1}
        {hasDuplicateOrder && ' ⚠️'}
      </span>

      {/* Squad buttons */}
      <div className={styles.squadButtons}>
        {desiredSquads.map((squadName) => {
          const pendingSquad = pendingSquads[sr?.id];
          const currentSquad = squad?.name;
          const isPending = pendingSquad === squadName;
          const isCurrent = currentSquad === squadName && !pendingSquad;

          return (
            <button
              key={squadName}
              className={`${styles.squadBtn} ${
                isCurrent ? styles.squadBtnActive : ''
              } ${isPending ? styles.squadBtnPending : ''}`}
              onClick={() => onSquadClick(sr, squadName)}
              disabled={!sr || applying}
              title={`Assign to Squad ${squadName}`}
            >
              {squadName}
            </button>
          );
        })}
      </div>

      {/* Team name with logo and colors */}
      <span className={styles.teamName}>
        <span
          className={styles.teamChip}
          style={{ backgroundColor: primaryColor }}
        >
          {teamLogo && (
            <img
              src={`${MEDIAURL}${teamLogo}`}
              alt=""
              className={styles.teamChipLogo}
            />
          )}
          {tricode}
        </span>
        <span className={styles.teamFullName}>
          {displayName(team?.name, team?.altNames)}
        </span>
      </span>

      {/* Roster dropdown */}
      <Form.Control
        as="select"
        className={styles.teamSelect}
        value={sr?.roster?.id || ''}
        onChange={(e) => onRosterChange(sr, team, e.target.value)}
      >
        <option value="">— Select Roster —</option>
        {teamRosters.map((r) => (
          <option key={r.id} value={r.id}>
            {r.title || 'Roster'}
            {r.default ? ' ★' : ''}
            {r.activeDate
              ? ` (${new Date(r.activeDate).toLocaleDateString()})`
              : ''}
          </option>
        ))}
      </Form.Control>

      {/* Lineup dropdown */}
      <Form.Control
        as="select"
        className={`${styles.teamSelect} ${
          lineups.length === 0 ? styles.selectEmpty : ''
        }`}
        value={sr?.lineupId || ''}
        onChange={(e) => sr && onLineupChange(sr, e.target.value)}
        disabled={!sr}
        title={lineups.length === 0 ? 'No lineups available' : undefined}
      >
        <option value="">
          {lineups.length === 0 ? '— No Lineups —' : '— Select Lineup —'}
        </option>
        {sortedLineups.map((l) => {
          const teamMatch = l.teamId === team?.id;
          const orderMatch = l.order === st.order;
          const isDuplicate = hasDuplicateKey(l);
          const lineupTeam = l.team;
          const lineupTricode =
            lineupTeam?.triCode || getTeamTricode(lineupTeam?.name);

          const indicators = [];
          if (l.individuals) indicators.push('indv');
          if (teamMatch) indicators.push('T✓');
          if (orderMatch) indicators.push('#✓');
          if (isDuplicate) indicators.push('⚠️');
          const matchStr =
            indicators.length > 0 ? ` [${indicators.join(' ')}]` : '';
          const title = l.forceTitle
            ? l.title
            : l.title || `Lineup ${l.order + 1}`;

          return (
            <option key={l.id} value={l.id}>
              {lineupTricode}
              {matchStr} {title}
            </option>
          );
        })}
      </Form.Control>

      {/* Remove button */}
      <button
        className={styles.teamRemove}
        onClick={() => sr && onDelete(sr)}
        disabled={!sr}
        title="Remove from session"
      >
        ×
      </button>
    </div>
  );
}
